// =============================================================
// Tolerance bands (LL / L / H / HH) — pure logic.
//
// Ported verbatim from the prototype measurement engine
// (scripts/perpetuum-mobile-app.jsx `measBand`, perpetuum-desktop-sections5.jsx):
//   value < LL  or value > HH  → alarm (red, haptic-error)
//   value < L   or value > H   → warn  (amber, haptic-warning)
//   otherwise                  → in-band (ok)
//
// Binding domain rule (PROJECT.md): "measurement bands LL/L/H/HH" /
// "tolerance-checked". A reading outside the band is never silently accepted.
// =============================================================

import type { MeasurementPoint } from "../types/index.js";

/** Result band for a tolerance check. "none" = no/invalid reading yet. */
export type ToleranceBand = "ok" | "warn" | "alarm" | "none";

/** The four threshold levels of a measurement point. */
export interface ToleranceThresholds {
  /** Alarm-low. */
  ll: number;
  /** Warning-low. */
  l: number;
  /** Warning-high. */
  h: number;
  /** Alarm-high. */
  hh: number;
}

export interface ToleranceResult {
  band: ToleranceBand;
  /** The parsed numeric value, or NaN when input was empty/non-numeric. */
  value: number;
}

/**
 * Validate a thresholds object. Requires LL <= L <= H <= HH.
 * (A malformed template should fail loudly, not mis-classify readings.)
 */
export function isValidThresholds(t: ToleranceThresholds): boolean {
  return t.ll <= t.l && t.l <= t.h && t.h <= t.hh;
}

/**
 * Classify a reading against LL/L/H/HH thresholds.
 *
 * @param thresholds  the point's four levels.
 * @param raw         the reading — number or string (empty/NaN → "none").
 */
export function classifyTolerance(
  thresholds: ToleranceThresholds,
  raw: number | string | null | undefined,
): ToleranceResult {
  const value = typeof raw === "number" ? raw : parseFloat(String(raw ?? ""));
  if (raw === "" || raw === null || raw === undefined || Number.isNaN(value)) {
    return { band: "none", value: NaN };
  }
  const { ll, l, h, hh } = thresholds;
  if (value < ll || value > hh) return { band: "alarm", value };
  if (value < l || value > h) return { band: "warn", value };
  return { band: "ok", value };
}

/** Classify a reading directly against a MeasurementPoint. */
export function classifyPoint(
  point: MeasurementPoint,
  raw: number | string | null | undefined,
): ToleranceResult {
  return classifyTolerance(point, raw);
}
